/**
 * Tenant Branding Component
 *
 * Displays the IlmIQ logo with optional tenant name.
 * When branding props (logoUrl, primaryColor, secondaryColor) are provided,
 * renders custom school branding instead of default IlmIQ branding.
 * Used on public pages like login, reset password, application form, etc.
 */

import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import {
  AutoStories as BookIcon,
  Psychology as BrainIcon,
} from '@mui/icons-material';

interface TenantBrandingProps {
  /** Tenant name to display below the logo */
  tenantName?: string;
  /** Whether tenant data is loading */
  isLoading?: boolean;
  /** Size variant */
  size?: 'small' | 'medium' | 'large';
  /** Show tenant name even if provided */
  showTenantName?: boolean;
  /** Custom logo URL (renders img instead of icon box) */
  logoUrl?: string | null;
  /** Primary brand color (hex) */
  primaryColor?: string;
  /** Secondary brand color (hex) */
  secondaryColor?: string;
}

const sizeConfig = {
  small: {
    logoWidth: 48,
    logoHeight: 40,
    logoPx: 0.5,
    iconSize: 20,
    borderRadius: '8px',
    appNameVariant: 'h6' as const,
    tenantVariant: 'caption' as const,
    imgMaxHeight: 40,
  },
  medium: {
    logoWidth: 60,
    logoHeight: 52,
    logoPx: 0.75,
    iconSize: 24,
    borderRadius: '10px',
    appNameVariant: 'h5' as const,
    tenantVariant: 'body2' as const,
    imgMaxHeight: 52,
  },
  large: {
    logoWidth: 72,
    logoHeight: 64,
    logoPx: 1,
    iconSize: 28,
    borderRadius: '12px',
    appNameVariant: 'h4' as const,
    tenantVariant: 'body1' as const,
    imgMaxHeight: 64,
  },
};

const DEFAULT_PRIMARY = '#667eea';
const DEFAULT_SECONDARY = '#764ba2';

export const TenantBranding: React.FC<TenantBrandingProps> = ({
  tenantName,
  isLoading = false,
  size = 'medium',
  showTenantName = true,
  logoUrl,
  primaryColor,
  secondaryColor,
}) => {
  const config = sizeConfig[size];
  const p = primaryColor || DEFAULT_PRIMARY;
  const s = secondaryColor || DEFAULT_SECONDARY;
  const gradient = `linear-gradient(135deg, ${p} 0%, ${s} 100%)`;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
      }}
    >
      {/* Logo */}
      {logoUrl ? (
        <Box
          component="img"
          src={logoUrl}
          alt={tenantName || 'School Logo'}
          sx={{
            maxHeight: config.imgMaxHeight,
            maxWidth: config.logoWidth * 2,
            objectFit: 'contain',
          }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: config.logoWidth,
            height: config.logoHeight,
            borderRadius: config.borderRadius,
            background: gradient,
            px: config.logoPx,
          }}
        >
          <BookIcon
            sx={{
              fontSize: config.iconSize,
              color: 'white',
            }}
          />
          <BrainIcon
            sx={{
              fontSize: config.iconSize,
              color: 'white',
            }}
          />
        </Box>
      )}

      {/* App Name and Tenant */}
      {!logoUrl && (
        <Box sx={{ textAlign: 'left' }}>
          <Typography
            variant={config.appNameVariant}
            component="div"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              background: gradient,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Ilm IQ
          </Typography>
          {showTenantName && (
            <>
              {isLoading ? (
                <Skeleton width={100} height={16} />
              ) : tenantName ? (
                <Typography
                  variant={config.tenantVariant}
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    lineHeight: 1,
                  }}
                >
                  {tenantName}
                </Typography>
              ) : null}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TenantBranding;
